import LabelSelectList from '@/components/LabelSelectList'
import PermissionsController from '@/components/PermissionsController'
import Redirect from '@/components/Redirect'
import SmsTemplatePreviewDialog from '@/components/SmsTemplatePreviewDialog'
import TableTabButton from '@/components/TableTabButton'
import usePermissions from '@/hooks/usePermissions'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Button from '@/shared/Button'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import AddCircle from '@mui/icons-material/AddCircle'
import Edit from '@mui/icons-material/Edit'
import Close from '@mui/icons-material/HighlightOff'
import Preview from '@mui/icons-material/Preview'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import get from 'lodash/get'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

const mapStateToProps = ({ smsTemplate, loading }) => ({
  smsTemplateList: smsTemplate.smsTemplateList,
  smsTemplateLabels: smsTemplate.smsTemplateLabels,
  isLoading: loading['smsTemplate/getSmsTemplates'],
  isLabelsLoading: loading['smsTemplate/getSmsTemplateLabels'],
  deleteLoading: loading['smsTemplate/deleteSmsTemplate'],
})

const fieldMap = {
  name: 'SMS_NAME',
  labelNames: 'SMS_LABELNAMES',
  difficulty: 'DIFFICULTY',
  lastUpdateTime: 'LASTUPDATE',
}

const SmsTemplate = ({
  dispatch,
  smsTemplateList,
  smsTemplateLabels,
  isLoading,
  isLabelsLoading,
  deleteLoading,
}) => {
  const { data, paging = {} } = smsTemplateList || {}
  const { data: labelData, paging: labelPaging = {} } = smsTemplateLabels

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [sort, setSort] = useState([{ field: 'name', sort: 'asc' }])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [labelPage, setLabelPage] = useState(0)
  const [deleteSmsTemplateData, setDeleteSmsTemplateData] = useState()
  const [deleteLabelData, setDeleteLabelData] = useState()
  const [selectedLabel, setSelectedLabel] = useState([])

  const [preview, setPreview] = useState()

  const routeParams = useParams()
  const { type = '' } = routeParams || {}

  const isSystem = type === 'system'

  const isSystemRef = useRef(isSystem)

  const canView = usePermissions(isSystem ? 'smsTempSystem_view' : 'smsTempPerson_view')
  const canCreate = usePermissions(isSystem ? 'smsTempSystem_create' : 'smsTempPerson_create')
  const canModify = usePermissions(isSystem ? 'smsTempSystem_modify' : 'smsTempPerson_modify')
  const canDelete = usePermissions(isSystem ? 'smsTempSystem_delete' : 'smsTempPerson_delete')

  const companyId = useSelector(state => state.auth.account?.companyId)

  useEffect(() => {
    if (isSystem !== isSystemRef) {
      setPage(0)
      setSort([{ field: 'name', sort: 'asc' }])
      setPageSize(10)
    }
  }, [isSystem])

  const form = useRef()

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const btnProps = {
    size: 'small',
    sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
  }
  const iconProps = {
    sx: {
      padding: 0.5,
    },
  }

  const cols = [
    {
      field: 'name',
      headerName: t('general.template_name'),
      width: 200,
    },
    ...(isSystem
      ? [
          {
            field: 'difficulty',
            headerName: t('general.difficulty'),
            width: 100,

            valueFormatter: ({ value }) => (value ? t(`general.${value?.toLowerCase?.()}`) : '-'),
          },
        ]
      : []),
    {
      field: 'labelNames',
      headerName: t('general.label'),
      width: 80,
    },
    {
      field: 'include',
      headerName: t('general.contain'),
      width: 80,
    },
    {
      field: 'lastUpdateTime',
      headerName: t('general.update_date'),
      width: 200,

      valueFormatter: ({ value }) => (value ? moment(value).format(DATE_TIME_FORMAT) : '-'),
    },
    {
      field: 'edit',
      hidden: !canModify,
      headerName: t('general.edit'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            navigate(`/phish_template_sms/edit`, { state: { ...row, type } })
          }}
        >
          <Edit {...iconProps} />
        </IconButton>
      ),
    },
    {
      field: 'preview',
      headerName: t('general.preview'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            setPreview(row.smsTempId)
          }}
        >
          <Preview {...iconProps} />
        </IconButton>
      ),
    },
    // {
    //   field: 'delete',
    //   hidden: !canDelete,
    //   headerName: t('general.delete'),
    //   width: 100,

    //   renderCell: ({ row }) => (
    //     <IconButton
    //       {...btnProps}
    //       disabled={isSystem && companyId !== 1}
    //       onClick={() => {
    //         setDeleteSmsTemplateData(row)
    //       }}
    //     >
    //       <Close {...iconProps} />
    //     </IconButton>
    //   ),
    // },
    isSystem && companyId !== 1
      ? null
      : {
          field: 'delete',
          hidden: !canDelete,
          headerName: t('general.delete'),
          width: 100,

          renderCell: ({ row }) => (
            <IconButton
              {...btnProps}
              disabled={isSystem && companyId !== 1}
              onClick={() => {
                setDeleteSmsTemplateData(row)
              }}
            >
              <Close {...iconProps} />
            </IconButton>
          ),
        },
  ].filter(Boolean)

  const fetchData = useCallback(() => {
    const formValues = form.current?.getValues()
    const { search } = formValues || {}

    const payload = {
      searchParam: search,
      labelId: selectedLabel,
      sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
      pageIndex: page,
      pageSize,
      type: isSystem ? 'S' : 'I',
    }

    dispatch({
      type: 'smsTemplate/getSmsTemplates',
      payload,
    })
  }, [dispatch, sort, page, pageSize, selectedLabel, isSystem, form])

  const deleteSmsTemplate = () => {
    if (deleteSmsTemplateData) {
      dispatch({
        type: 'smsTemplate/deleteSmsTemplate',
        payload: { smsTempId: deleteSmsTemplateData.smsTempId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.email_template_deleted', {
                email_template: deleteSmsTemplateData.name,
              }),
              severity: 'success',
            },
          })
          setDeleteSmsTemplateData()
          fetchData()
        },
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [sort, page, pageSize, fetchData])

  const getLabels = () => {
    dispatch({
      type: 'smsTemplate/getSmsTemplateLabels',
      payload: { type: isSystem ? 'S' : 'I', pageIndex: labelPage, pageSize: 10 },
    })
  }

  const deleteSmsTemplateLabel = () => {
    if (deleteLabelData) {
      const { labelId, labelName } = deleteLabelData
      dispatch({
        type: 'smsTemplate/deleteSmsTemplateLabel',
        payload: { labelId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.email_template_deleted', {
                email_template: labelName,
              }),
              severity: 'success',
            },
          })
          setDeleteLabelData()
          getLabels()
        },
      })
    }
  }

  useEffect(() => {
    getLabels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystem, labelPage, dispatch])

  if (!canView) return <Redirect />

  return (
    <Container maxWidth={false} sx={{ flex: 1, display: 'flex' }}>
      <Grid container spacing={2} flexDirection="column">
        <Grid item>
          <Grid container>
            <Grid item>
              <FormCreator
                ref={form}
                formItems={[
                  ...(canCreate && (companyId === 1 || !isSystem)
                    ? [
                        {
                          component: () => (
                            <Button
                              variant="text"
                              color="secondary_80"
                              endIcon={<AddCircle />}
                              onClick={() => {
                                navigate(
                                  `/phish_template_sms/${
                                    isSystem ? 'system' : 'individual'
                                  }/create`,
                                )
                              }}
                            >
                              {t('general.add_template')}
                            </Button>
                          ),
                        },
                      ]
                    : []),
                  {
                    label: t('general.quick_search'),
                    name: 'search',
                    defaultValue: '',
                    inputConfig: {
                      onSubmit: async () => {
                        setPage(0)
                        fetchData()
                      },
                    },
                  },
                ]}
                flexDirection="row"
                submitComponent={() => null}
                resetComponent={() => null}
              />
            </Grid>
          </Grid>

          <Grid container alignItems="center" justifyContent="space-between" py={2}>
            <Grid item>
              <LabelSelectList
                idKey="labelId"
                nameKey="labelName"
                options={labelData ?? []}
                paging={labelPaging}
                page={labelPage}
                onPageChange={val => {
                  if (!isLabelsLoading) {
                    setLabelPage(val)
                  }
                }}
                onDelete={setDeleteLabelData}
                selected={selectedLabel}
                onSelect={label => {
                  if (selectedLabel === label) {
                    setSelectedLabel()
                  } else {
                    setSelectedLabel(label)
                  }
                }}
              />
            </Grid>
            <Grid item>
              <Stack direction="row">
                <Box flex={1} />
                <PermissionsController permission="smsTempSystem_view">
                  <TableTabButton
                    selected={isSystem}
                    onClick={() => {
                      navigate('/phish_template_sms/system', { replace: true })
                      setSelectedLabel([])
                      setLabelPage(0)
                    }}
                    label={t('menu.system')}
                  />
                </PermissionsController>
                <PermissionsController permission="smsTempPerson_view">
                  <TableTabButton
                    selected={!isSystem}
                    onClick={() => {
                      navigate('/phish_template_sms/individual', { replace: true })
                      setSelectedLabel([])
                      setLabelPage(0)
                    }}
                    label={t('menu.individual')}
                  />
                </PermissionsController>
              </Stack>
            </Grid>
          </Grid>
          <Table
            minHeight={400}
            columns={cols}
            rows={data || []}
            dataGridConfig={{
              sortingMode: 'server',
              sortingOrder: ['desc', 'asc'],
              sortModel: sort,
              onSortModelChange: newSort => setSort(newSort),
              getRowId: row => row.smsTempId,
              loading: isLoading,
            }}
            noRowsLabel={t('errors.no_sms_templates')}
            pagination={pagination}
          />
        </Grid>
      </Grid>

      <SmsTemplatePreviewDialog open={!!preview} smsTempId={preview} onClose={() => setPreview()} />

      <ConfirmDialog
        open={!!deleteSmsTemplateData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteSmsTemplateData()
        }}
        onSubmit={() => {
          deleteSmsTemplate()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>{`${deleteSmsTemplateData?.name}${t('messages.info')}?`}</Typography>
        </Stack>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!deleteLabelData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteLabelData()
        }}
        onSubmit={() => {
          deleteSmsTemplateLabel()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>{`${deleteLabelData?.labelName}${t('messages.info')}?`}</Typography>
        </Stack>
      </ConfirmDialog>
    </Container>
  )
}

export default connect(mapStateToProps)(SmsTemplate)
